/**
 * The platform's access to this organisation (docs/operator-access.md), at /platform-access: searchable, filtered by
 * kind and period, summarised, explained and exported (routes/operatorAccess.ts).
 *
 * On Mojo Up AI Cloud it is "Support access": each time the support team opened this organisation's data, why, for
 * how long, and what they looked at under it. The service's own maintenance names no organisation and the API does
 * not send it here; opening or closing access is the operators' (Operate).
 *
 * An Enterprise deployment has no support staff of the platform's, so the page is what the service's own work looked
 * up across organisations: grouped by what happened, each explained (what it is, why it could not ask one
 * organisation at a time, how much it read), then every entry, which opens its detail in a sheet.
 *
 * The data column (Layout.tsx): the filters in the header (a search and two choices), stats, then list groups;
 * Export sits on the list it exports.
 */

import { ArrowDownToLine, Clock, Eye, Gear, LayoutList, LockOpen, PersonMagnifier, ShieldKeyhole } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useEffect, useState } from 'react';
import { explainPlatformRead, PLATFORM_READ_DEPTH_WORDS, type OperatorAccessEntry, type OperatorAccessGroup, type OperatorAccessShow } from '@aioe/shared';

import { operatorAccessRoutes, problemText, type OperatorAccessFilter } from '../api/client.ts';
import { accessWhen as when } from '../components/cloud/StaffAccessGrants.tsx';
import { ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import type { ArtTone } from '../components/ui/Art.tsx';
import { FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';

const tone: ArtTone = 'slate';

const PERIODS = { day: { label: 'Last 24 hours', days: 1 }, week: { label: 'Last 7 days', days: 7 }, month: { label: 'Last 30 days', days: 30 }, quarter: { label: 'Last 90 days', days: 90 }, all: { label: 'Everything kept', days: 400 } } as const;
type Period = keyof typeof PERIODS;
const PERIOD_OPTIONS = Object.fromEntries(Object.entries(PERIODS).map(([k, v]) => [k, v.label])) as Record<Period, string>;

/** The filters, with the search settled a moment after typing stops. */
function useFilters(defaultShow: OperatorAccessShow) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState<OperatorAccessShow>(defaultShow);
  const [period, setPeriod] = useState<Period>('month');
  const [what, setWhat] = useState<string>();
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);
  // A period is "the last n days" from when it was chosen, so a page left open does not drift.
  const [since, setSince] = useState(() => new Date(Date.now() - PERIODS.month.days * 86_400_000).toISOString());
  const choosePeriod = (p: Period) => {
    setPeriod(p);
    setSince(new Date(Date.now() - PERIODS[p].days * 86_400_000).toISOString());
  };
  const filter: OperatorAccessFilter = { show, since, ...(q ? { q } : {}), ...(what !== undefined ? { what } : {}) };
  return { typed, setTyped, show, setShow, period, choosePeriod, what, setWhat, filter, key: JSON.stringify(filter) };
}

/** The record under the filters, newest first, with "Show older" paging by the last row's time. */
function useAccessLog(filter: OperatorAccessFilter, key: string) {
  const log = useLoad(() => operatorAccessRoutes.log(filter), [key]);
  // Older pages belong to the filter they were read under; another filter starts from its first page.
  const [paged, setPaged] = useState<{ key: string; rows: OperatorAccessEntry[]; next: string | undefined }>();
  const [error, setError] = useState<string>();
  const mine = paged?.key === key ? paged : undefined;
  const cursor = mine ? mine.next : log.data?.next;
  const more = async () => {
    if (!cursor) return;
    setError(undefined);
    try {
      const page = await operatorAccessRoutes.log(filter, cursor);
      setPaged({ key, rows: [...(mine?.rows ?? []), ...page.entries], next: page.next });
    } catch (e) {
      setError(problemText(e));
    }
  };
  return { loaded: !!log.data, entries: [...(log.data?.entries ?? []), ...(mine?.rows ?? [])], cursor, more, error: error ?? log.error };
}

function ShowOlder({ onPress }: { onPress: () => void }) {
  return (
    <ListBlock>
      <Button size="sm" variant="ghost" onPress={onPress}>
        <Clock className="size-4" />
        Show older
      </Button>
    </ListBlock>
  );
}

/** Export: the entries under the filters, as a spreadsheet file. */
function ExportButton({ filter }: { filter: OperatorAccessFilter }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <>
      {error ? <span className="text-xs text-danger">{error}</span> : null}
      <Button
        size="sm"
        variant="secondary"
        isPending={busy}
        onPress={() => {
          setBusy(true);
          setError(undefined);
          operatorAccessRoutes
            .exportCsv(filter)
            .catch((e: unknown) => setError(problemText(e)))
            .finally(() => setBusy(false));
        }}
      >
        <ArrowDownToLine className="size-4" />
        Export
      </Button>
    </>
  );
}

export function OperatorAccessPage() {
  return isCloud() ? <SupportAccessView /> : <PlatformAccessView />;
}

// ── Mojo Up AI Cloud: support access ───────────────────────────────────────────────────────────────

/** What a read under a grant looked at, in the customer's words (the gated routes in routes/operatorAccess.ts). */
function lookedAt(what: string): string {
  const path = what.replace(/^[A-Z]+ /, '').split('?')[0]!;
  if (/^\/operator\/v1\/organisations\/[^/]+$/.test(path)) return "your organisation's summary (its name, members and machine count)";
  if (/^\/operator\/v1\/organisations\/[^/]+\/audit$/.test(path)) return 'your audit trail';
  return "your organisation's data";
}

const joinWords = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

const SUPPORT_SHOW: Partial<Record<OperatorAccessShow, string>> = { staff: 'Everything', grant: 'Access opened', read: 'What was looked at' };

function SupportAccessView() {
  const f = useFilters('staff');
  const log = useAccessLog(f.filter, f.key);
  const summary = useLoad(() => operatorAccessRoutes.summary(f.filter), [f.key]);
  const nowIso = new Date().toISOString();
  const grants = log.entries.filter((e) => e.kind === 'grant');
  const readsOf = new Map<string, OperatorAccessEntry[]>();
  const loose: OperatorAccessEntry[] = [];
  const grantIds = new Set(grants.map((g) => g.id));
  for (const e of log.entries) {
    if (e.kind !== 'read') continue;
    if (e.grantId && grantIds.has(e.grantId)) readsOf.set(e.grantId, [...(readsOf.get(e.grantId) ?? []), e]);
    else loose.push(e);
  }
  const isOpen = (g: OperatorAccessEntry) => !g.closedAt && !!g.expiresAt && g.expiresAt > nowIso;
  // Grants and the reads whose grant is on an older page or filtered out, newest first.
  const rows = [...grants, ...loose].sort((a, b) => b.at.localeCompare(a.at));
  const t = summary.data?.totals;
  const filtered = !!f.filter.q || f.show !== 'staff';

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<ShieldKeyhole />}
        title="Support access"
        subtitle="Each time our support team opened your organisation’s data: why, for how long, and what they looked at."
        actions={
          <>
            <SearchBox label="Search" value={f.typed} onChange={f.setTyped} placeholder="Search a reason or a name" />
            <ChoiceSelect label="What to show" className="w-44 max-w-full" value={f.show} options={SUPPORT_SHOW as Record<OperatorAccessShow, string>} onChange={f.setShow} />
            <ChoiceSelect label="Period" className="w-40 max-w-full" value={f.period} options={PERIOD_OPTIONS} onChange={f.choosePeriod} />
          </>
        }
      />
      <Explainer id="support-access" tone={tone} icon={<ShieldKeyhole />} title="How support access works" summary="Support opens your data only to help with a request you raised">
        <p>Our support team can open your organisation’s data only to help with a request you raised, for at most four hours, and only after recording why. Access closes by itself when the time is up.</p>
        <p>Each time is listed here with the reason and what was looked at under it, kept for 400 days, and is on your audit trail too.</p>
      </Explainer>
      <PartBoundary key="access" name="Support access" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<LockOpen />} label="Times opened" value={t?.grants} detail={PERIODS[f.period].label} />
          <Stat tone={tone} icon={<Eye />} label="Things looked at" value={t?.reads} detail="Under that access" />
          <Stat tone={tone} icon={<ShieldKeyhole />} label="Open now" value={t?.openNow} detail={t?.openNow ? 'Closes by itself within four hours' : 'Nobody has access'} needsYou={!!t?.openNow} />
        </div>
        <ErrorNote error={log.error ?? summary.error} />
        <ListGroup
          title="Access to your organisation"
          count={log.loaded ? rows.length : undefined}
          description="Newest first, kept for 400 days, and on your audit trail too."
          actions={<ExportButton filter={f.filter} />}
          empty={
            log.loaded && rows.length === 0 ? (
              filtered ? (
                <Empty tone={tone} icon={<PersonMagnifier />} title="Nothing matches" body="Nothing in this period matches the search or the filter." />
              ) : (
                <Empty tone={tone} icon={<PersonMagnifier />} title="Support has not opened your data" body="Our support team can open your organisation’s data only to help with a request you raised, for at most four hours, and only after recording why. Each time shows here." />
              )
            ) : undefined
          }
        >
          {!log.loaded ? <ListBlock className="text-sm text-muted">Reading the record…</ListBlock> : null}
          {rows.map((e) => {
            const who = e.operator?.name ?? 'AI Cloud support';
            if (e.kind === 'read') {
              return (
                <ListRow
                  key={e.id}
                  title={`${who} looked at ${lookedAt(e.what)}`}
                  description={
                    <>
                      {e.why ? <span className="block text-foreground">{e.why}</span> : null}
                      <span className="block">{when(e.at)}</span>
                    </>
                  }
                />
              );
            }
            const reads = readsOf.get(e.id) ?? [];
            const seen = joinWords([...new Set(reads.map((r) => lookedAt(r.what)))]);
            const open = isOpen(e);
            return (
              <ListRow
                key={e.id}
                title={`${who} opened access`}
                description={
                  <>
                    {e.why ? <span className="block text-foreground">{e.why}</span> : null}
                    <span className="block">{reads.length === 0 ? 'Looked at nothing yet.' : `Looked at ${seen} (${reads.length === 1 ? 'once' : `${reads.length} times`}).`}</span>
                    <span className="block">
                      {when(e.at)}
                      {e.closedAt ? `, closed early ${when(e.closedAt)}` : e.expiresAt ? `${open ? ', until' : ', ended'} ${when(e.expiresAt)}` : ''}
                    </span>
                  </>
                }
                status={open ? <StatusMark state="online" label="Open now" /> : <StatusMark state="disabled" label="Closed" />}
              />
            );
          })}
          {log.cursor ? <ShowOlder onPress={() => void log.more()} /> : null}
        </ListGroup>
      </PartBoundary>
    </PageColumn>
  );
}

// ── Enterprise: the service's own work, explained ──────────────────────────────────────────────────

/** A row's words: a job explained, or an operator's grant or read as it was. */
function described(kind: OperatorAccessEntry['kind'], what: string, why?: string) {
  if (kind === 'job') return explainPlatformRead(what);
  return kind === 'grant'
    ? { area: 'Support access', title: 'An operator opened access to the organisation', text: why ?? 'An operator of this service opened access, for at most four hours.', reads: 'content' as const }
    : { area: 'Support access', title: `An operator read ${what.replace(/^GET /, '')}`, text: why ?? 'Read under open access.', reads: 'content' as const };
}

const DEPTH_STATE = { identifiers: 'online', settings: 'online', content: 'medium' } as const;

function GroupRow({ g, onPick }: { g: OperatorAccessGroup; onPick: () => void }) {
  const d = described(g.kind, g.what);
  return (
    <ListRow
      onPress={onPick}
      title={d.title}
      description={
        <>
          <span className="block">{d.text}</span>
          <span className="block">
            {d.area} · {g.count === 1 ? 'once' : `${g.count.toLocaleString()} times`}, last {timeAgo(g.lastAt)}
          </span>
        </>
      }
      status={<StatusMark state={DEPTH_STATE[d.reads]} label={PLATFORM_READ_DEPTH_WORDS[d.reads]} />}
    />
  );
}

/** One entry's detail, in a sheet: what it means, what it reads, and how it was recorded. */
function EntrySheet({ e, onClose }: { e: OperatorAccessEntry; onClose: () => void }) {
  const d = described(e.kind, e.what, e.why);
  return (
    <FormSheet
      width="lg"
      title={d.title}
      description={`${e.principal === 'platform' ? 'The service' : (e.operator?.name ?? 'An operator')} · ${when(e.at)}`}
      onClose={onClose}
      footer={
        <Button size="sm" variant="tertiary" onPress={onClose}>
          Close
        </Button>
      }
    >
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
        <dt className="text-muted">What it means</dt>
        <dd className="min-w-0 break-words">{d.text}</dd>
        <dt className="text-muted">What it reads</dt>
        <dd>
          <StatusMark state={DEPTH_STATE[d.reads]} label={PLATFORM_READ_DEPTH_WORDS[d.reads]} />
        </dd>
        <dt className="text-muted">Part of the service</dt>
        <dd>{d.area}</dd>
        <dt className="text-muted">Recorded as</dt>
        <dd className="mono min-w-0 break-words text-xs">{e.what}</dd>
        {e.kind === 'job' ? (
          <>
            <dt className="text-muted">Organisations</dt>
            <dd>All of them at once; it then worked in each one separately</dd>
          </>
        ) : null}
        {e.requestId ? (
          <>
            <dt className="text-muted">Request id</dt>
            <dd className="mono min-w-0 break-words text-xs">{e.requestId}</dd>
          </>
        ) : null}
      </dl>
    </FormSheet>
  );
}

const PLATFORM_SHOW: Partial<Record<OperatorAccessShow, string>> = { all: 'Everything', job: 'The service’s own work', staff: 'Operators' };

function PlatformAccessView() {
  const f = useFilters('all');
  const log = useAccessLog(f.filter, f.key);
  // The summary is of the whole period under the search, so a picked row does not shrink it to one group.
  const { what: _picked, ...whole } = f.filter;
  const summary = useLoad(() => operatorAccessRoutes.summary(whole), [JSON.stringify(whole)]);
  const s = summary.data;
  const groups = s?.groups ?? [];
  const content = groups.filter((g) => described(g.kind, g.what).reads === 'content').reduce((n, g) => n + g.count, 0);
  const latest = groups.reduce<string | undefined>((m, g) => (!m || g.lastAt > m ? g.lastAt : m), undefined);
  const picked = f.what !== undefined ? described(groups.find((g) => g.what === f.what)?.kind ?? 'job', f.what).title : undefined;
  const filtered = !!f.filter.q || f.show !== 'all';
  const [opened, setOpened] = useState<OperatorAccessEntry>();

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<ShieldKeyhole />}
        title="Platform access"
        subtitle="What the service’s own work looked up across organisations, with what it read and why."
        actions={
          <>
            <SearchBox label="Search" value={f.typed} onChange={f.setTyped} placeholder="Search, such as backups" />
            {s && s.totals.grants + s.totals.reads > 0 ? <ChoiceSelect label="What to show" className="w-48 max-w-full" value={f.show} options={PLATFORM_SHOW as Record<OperatorAccessShow, string>} onChange={f.setShow} /> : null}
            <ChoiceSelect label="Period" className="w-40 max-w-full" value={f.period} options={PERIOD_OPTIONS} onChange={f.choosePeriod} />
          </>
        }
      />
      <Explainer id="platform-access" tone={tone} icon={<ShieldKeyhole />} title="How platform access works" summary="The service’s own maintenance, such as clearing out expired backups; never a person reading your data">
        <p>Some of the service’s own work has to look across every organisation at once: finding expired backups to clear out, or the next labelling job in a queue. It cannot ask one organisation at a time, because it does not yet know which ones have work waiting.</p>
        <p>Each kind of lookup is listed with what it is and how much it reads: identifiers only, settings, or organisation data. It then does its work inside each organisation separately, as everything else does. None of it is a person reading your data.</p>
      </Explainer>
      <PartBoundary key="access" name="Platform access" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<Gear />} label="Lookups" value={s ? s.totals.jobs + s.totals.reads + s.totals.grants : undefined} detail={PERIODS[f.period].label} />
          <Stat tone={tone} icon={<LayoutList />} label="Kinds" value={s ? groups.length : undefined} detail="Different things the service did" />
          <Stat tone={tone} icon={<Eye />} label="Read your data" value={s ? content : undefined} detail={content ? 'Opened organisation data' : 'Identifiers and settings only'} needsYou={content > 0} />
          <Stat tone={tone} icon={<Clock />} label="Latest" value={latest ? timeAgo(latest) : s ? 'None' : undefined} detail={latest ? when(latest) : ''} />
        </div>
        <ErrorNote error={log.error ?? summary.error} />
        <ListGroup
          title="What happened"
          count={s ? groups.length : undefined}
          description="Grouped, most frequent first. Open one to see each time it happened."
          empty={s && groups.length === 0 ? <Empty tone={tone} icon={<LayoutList />} title={filtered ? 'Nothing matches' : 'Nothing in this period'} body={filtered ? 'Nothing in this period matches the search or the filter.' : 'When the service looks across organisations, each kind shows here with what it means.'} /> : undefined}
        >
          {!s ? <ListBlock className="text-sm text-muted">Reading the record…</ListBlock> : null}
          {groups.map((g) => (
            <GroupRow key={`${g.kind}:${g.what}`} g={g} onPick={() => f.setWhat(g.what)} />
          ))}
        </ListGroup>
        <ListGroup
          title="Every entry"
          count={log.loaded ? log.entries.length : undefined}
          description={picked ? `Showing: ${picked}.` : 'Newest first; open one for its detail. Repeats within a minute are one entry.'}
          actions={
            <>
              {picked ? (
                <Button size="sm" variant="ghost" onPress={() => f.setWhat(undefined)}>
                  Show all
                </Button>
              ) : null}
              <ExportButton filter={f.filter} />
            </>
          }
          empty={log.loaded && log.entries.length === 0 ? <Empty tone={tone} icon={<PersonMagnifier />} title="Nothing matches" body="Nothing in this period matches." /> : undefined}
        >
          {log.entries.map((e) => {
            const d = described(e.kind, e.what, e.why);
            return <ListRow key={e.id} onPress={() => setOpened(e)} title={d.title} description={`${e.principal === 'platform' ? 'The service' : (e.operator?.name ?? 'An operator')} · ${when(e.at)}`} />;
          })}
          {log.cursor ? <ShowOlder onPress={() => void log.more()} /> : null}
        </ListGroup>
      </PartBoundary>
      {opened ? <EntrySheet e={opened} onClose={() => setOpened(undefined)} /> : null}
    </PageColumn>
  );
}
